import Tealium from '@4tn/talpatv-video-analytics'
import { version } from '@4tn/tv-npm-player/dist/config'
import { ConsentData } from '../../typings/'
import { EU_CONSENT_KEY, CONSENTED_VENDORS_KEY, C_CONSENT_KEY } from './consent'
import { inIframe } from './inIframe'
import { getCookie } from './cookie'
import { log, GREY, YELLOW, GREEN } from '.'

declare global {
  interface Window {
    TN_GDPR_BYPASS?: boolean
  }
}

export function sendConsentToTealium(consentData: ConsentData, attempt = 0): void {
  const euConsentCookie = getCookie(EU_CONSENT_KEY)
  const cConsentCookie = getCookie(C_CONSENT_KEY) || ''
  const consentedVendorsCookie = getCookie(CONSENTED_VENDORS_KEY)
  if (euConsentCookie === consentData.tcString && consentData.tcStringCustom === cConsentCookie) {
    log(`:: Consent cookie already set ::`, GREEN, {
      ...consentData,
      euConsentCookie,
      cConsentCookie,
      consentedVendorsCookie,
    })
    return
  }

  if (attempt > 20) {
    log(":: sendConsentToTealium > couldn't find setConsentedVendors", YELLOW)
    return
  }
  if (typeof (window as any).setConsentedVendors !== 'function') {
    attempt += 1
    log(':: sendConsentToTealium > wait for setConsentedVendors', GREY)
    setTimeout(sendConsentToTealium, 100, consentData, attempt)
    return
  }
  log(':: sendConsentToTealium ::', GREY, {
    ...consentData,
    euConsentCookie,
    cConsentCookie,
    consentedVendorsCookie,
  })
  ;(window as any).setConsentedVendors(consentData.tcString, consentData.tcStringCustom, true)
}
async function loadTaq({ environment }: any): Promise<void> {
  return new Promise((resolve, reject) => {
    window.Taq = window.Taq || []
    log('Loading Taq')

    window.Taq.push([
      'setup',
      {
        account: 'talpa',
        profile: 'tv-embed-web',
        environment,
        dataLayerVersion: 1.2,
        dataLayer: {
          app_name: 'embed player',
          app_version: version,
        },
        extensions: {
          EventLog: {
            keepmax: 50,
          },
          NoBo: true,
        },
      },
    ])

    const script = document.createElement('script')
    script.type = 'text/javascript'
    script.async = true
    script.src = `https://static.talpanetwork.com/prod/js/components/talpa-analytics/talpa-analytics-browser-1.2.js`
    script.onload = (): void => {
      log('Tealium loaded successfully')
      Tealium.tealiumState = 'useTaq'
      Tealium.setEventsFromQueue()
      if (!inIframe && !window.TN_GDPR_BYPASS) {
        // Wait for tcf to be loaded by Tealium
        const retryLimit = 10
        let retry = 0
        const interval = setInterval(async () => {
          if (typeof (window as any).__tcfapi === 'function' || retry >= retryLimit) {
            ;(window as any).ON_TALPA_DOMAIN = true
            clearInterval(interval)
            resolve()
            return
          }
          retry++
        }, 100)
      } else {
        resolve()
      }
    }
    script.onerror = (): void => {
      reject('Taq could not be loaded.')
    }
    document.getElementsByTagName('HEAD')[0].appendChild(script)
  })
}

export const initTealium = async (useVta: boolean, environment: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    const tealiumMethog = useVta ? Tealium.setup : loadTaq
    log(
      `%c == ${useVta ? 'meten is weten active' : 'TAQ monitoring'} ==`,
      'font-weight: bold; background-color: black; color: white',
    )

    tealiumMethog({
      account: 'talpa',
      profile: 'video-kijk-embed',
      environment,
      defaultLayer: {
        app_name: 'embed player',
        app_version: version,
      },
    })
      .then(() => {
        log('Tealium loaded successfully')
        if (!inIframe && !window.TN_GDPR_BYPASS) {
          // Wait for tcf to be loaded by Tealium
          const retryLimit = 10
          let retry = 0
          const interval = setInterval(async () => {
            console.log('typeof __tcfapi', typeof (window as any).__tcfapi)
            if (typeof (window as any).__tcfapi === 'function' || retry >= retryLimit) {
              ;(window as any).ON_TALPA_DOMAIN = true
              clearInterval(interval)
              resolve()
              return
            }
            retry++
          }, 100)
        } else {
          resolve()
        }
      })
      .catch((error) => {
        reject(`Taq could not be loaded: ${error}`)
      })
  })
}

export function setDataLayer(data: { [key: string]: string | boolean | null }): void {
  Tealium.setDataLayer(data)
}

export function trackView(data: { [key: string]: string | boolean | null }): void {
  Tealium.view({ event_category: 'PAGEVIEW', event_name: 'pageview', ...data })
}

export const trackClick = (label = ''): void => {
  Tealium.link({
    event_name: 'player_click',
    event_category: 'player',
    event_label: label,
  })
}
